/**
 * Time utility functions for generating and formatting time intervals
 */

export interface TimeOption {
  value: string; // 24-hour format: "09:00", "14:30"
  label: string; // Display format: "9:00 AM", "2:30 PM"
}

/**
 * Generate time intervals for a full day
 * @param intervalMinutes - Interval in minutes (default: 15)
 * @param use24Hour - Use 24-hour format for labels (default: false)
 * @returns Array of time options
 */
export function generateTimeIntervals(
  intervalMinutes: number = 15,
  use24Hour: boolean = false
): TimeOption[] {
  const times: TimeOption[] = [];

  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += intervalMinutes) {
      const timeValue = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      const timeLabel = use24Hour
        ? timeValue
        : format12Hour(hour, minute);

      times.push({ value: timeValue, label: timeLabel });
    }
  }

  return times;
}

/**
 * Generate time intervals within a specific range
 * @param startHour - Start hour (0-23)
 * @param endHour - End hour (0-23)
 * @param intervalMinutes - Interval in minutes (default: 15)
 * @param use24Hour - Use 24-hour format for labels (default: false)
 * @returns Array of time options
 */
export function generateTimeIntervalsInRange(
  startHour: number,
  endHour: number,
  intervalMinutes: number = 15,
  use24Hour: boolean = false
): TimeOption[] {
  const times: TimeOption[] = [];

  for (let hour = startHour; hour <= endHour; hour++) {
    const minuteEnd = hour === endHour ? 0 : 60; // For end hour, only include :00

    for (let minute = 0; minute < minuteEnd; minute += intervalMinutes) {
      const timeValue = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      const timeLabel = use24Hour
        ? timeValue
        : format12Hour(hour, minute);

      times.push({ value: timeValue, label: timeLabel });
    }
  }

  // Add the end hour
  if (endHour <= 23) {
    const timeValue = `${endHour.toString().padStart(2, '0')}:00`;
    const timeLabel = use24Hour ? timeValue : format12Hour(endHour, 0);
    times.push({ value: timeValue, label: timeLabel });
  }

  return times;
}

/**
 * Format time in 12-hour format with AM/PM
 * @param hour - Hour (0-23)
 * @param minute - Minute (0-59)
 * @returns Formatted time string (e.g., "9:00 AM", "2:30 PM")
 */
function format12Hour(hour: number, minute: number): string {
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const minuteStr = minute.toString().padStart(2, '0');

  return `${hour12}:${minuteStr} ${period}`;
}

/**
 * Convert 24-hour time string to 12-hour format
 * @param time24 - Time in 24-hour format (e.g., "14:30")
 * @returns Time in 12-hour format (e.g., "2:30 PM")
 */
export function convertTo12Hour(time24: string): string {
  const [hourStr, minuteStr] = time24.split(':');
  const hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);

  return format12Hour(hour, minute);
}

/**
 * Check if a time string is valid
 * @param time - Time string in HH:MM format
 * @returns True if valid
 */
export function isValidTime(time: string): boolean {
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return timeRegex.test(time);
}

/**
 * Round time to nearest interval
 * @param time - Time string in HH:MM format
 * @param intervalMinutes - Interval in minutes
 * @returns Rounded time string
 */
export function roundTimeToInterval(time: string, intervalMinutes: number = 15): string {
  const [hourStr, minuteStr] = time.split(':');
  const hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);

  const roundedMinute = Math.round(minute / intervalMinutes) * intervalMinutes;
  const finalMinute = roundedMinute === 60 ? 0 : roundedMinute;
  const finalHour = roundedMinute === 60 ? (hour + 1) % 24 : hour;

  return `${finalHour.toString().padStart(2, '0')}:${finalMinute.toString().padStart(2, '0')}`;
}

/**
 * Generate time intervals within a time range defined by time strings
 * @param startTime - Start time in HH:MM format (e.g., "09:00")
 * @param endTime - End time in HH:MM format (e.g., "17:00")
 * @param intervalMinutes - Interval in minutes (default: 15)
 * @param use24Hour - Use 24-hour format for labels (default: false)
 * @returns Array of time options within the range
 */
export function generateTimeIntervalsForRange(
  startTime: string,
  endTime: string,
  intervalMinutes: number = 15,
  use24Hour: boolean = false
): TimeOption[] {
  // Parse start and end times
  const [startHour, startMinute] = startTime.split(':').map(s => parseInt(s, 10));
  const [endHour, endMinute] = endTime.split(':').map(s => parseInt(s, 10));

  const times: TimeOption[] = [];

  // Convert times to total minutes for easier comparison
  const startTotalMinutes = startHour * 60 + startMinute;
  const endTotalMinutes = endHour * 60 + endMinute;

  // Generate time slots from start to end
  let currentMinutes = startTotalMinutes;

  while (currentMinutes <= endTotalMinutes) {
    const hour = Math.floor(currentMinutes / 60);
    const minute = currentMinutes % 60;

    // Don't go past 23:59
    if (hour >= 24) break;

    const timeValue = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    const timeLabel = use24Hour ? timeValue : format12Hour(hour, minute);

    times.push({ value: timeValue, label: timeLabel });

    currentMinutes += intervalMinutes;
  }

  return times;
}

/**
 * Adds a duration to a 24-hour time string. Used in group-class flows where the
 * UI shows the start time and a read-only end time computed from the service's
 * duration_minutes. Wraps past midnight (returns 24-hour HH:MM, capped at 23:59).
 *
 * @param startHHMM "HH:MM" 24-hour
 * @param durationMinutes positive integer minutes
 * @returns "HH:MM" 24-hour
 */
export function endTimeFromStart(startHHMM: string, durationMinutes: number): string {
  if (!isValidTime(startHHMM) || !Number.isFinite(durationMinutes) || durationMinutes < 0) {
    return startHHMM;
  }
  const [h, m] = startHHMM.split(':').map(Number);
  const total = h * 60 + m + Math.floor(durationMinutes);
  const hour = Math.min(23, Math.floor(total / 60));
  const minute = total >= 24 * 60 ? 59 : total % 60;
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
}

